import { useCallback, useRef, useState } from "react";

type UseIsTruncatedResult<T extends HTMLElement> = {
  /** Attach to the element that may be clipped (e.g. one with CSS ellipsis). */
  ref: (node: T | null) => void;
  /** True while the element's content overflows its box (horizontally or vertically). */
  isTruncated: boolean;
};

/**
 * Detects whether an element's content is visually clipped — `scrollWidth >
 * clientWidth` (horizontal ellipsis) or `scrollHeight > clientHeight` (line
 * clamp). Useful to reveal a tooltip with the full text only when it's actually
 * cut off.
 *
 * Returns a callback `ref` and the live `isTruncated` flag. It re-measures on:
 * - mount / node change (the callback ref re-runs),
 * - element resize (ResizeObserver),
 * - content change without a resize (MutationObserver on the subtree),
 * so it stays correct for responsive layouts and dynamic text.
 *
 * @example
 *   const { ref, isTruncated } = useIsTruncated<HTMLDivElement>();
 *   <Tooltip title={isTruncated ? text : undefined}>
 *     <Typography ref={ref} ellipsis>{text}</Typography>
 *   </Tooltip>
 */
export function useIsTruncated<
  T extends HTMLElement = HTMLElement,
>(): UseIsTruncatedResult<T> {
  const [isTruncated, setIsTruncated] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  const ref = useCallback((node: T | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    if (!node) {
      return;
    }

    const measure = () => {
      setIsTruncated(
        node.scrollWidth > node.clientWidth ||
          node.scrollHeight > node.clientHeight
      );
    };
    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(node);

    // Content can change without the box resizing (fixed-width element, updated
    // text), so also watch the subtree for text/child mutations.
    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(node, {
      characterData: true,
      childList: true,
      subtree: true,
    });

    cleanupRef.current = () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return { ref, isTruncated };
}
